import React from 'react';
import { Box, Card, CardContent, Typography } from '@mui/material';
import { UpcomingProject } from '../types';
import { useTheme as useAppTheme } from '../../theme/ThemeProvider';
import { useIsMobile } from '../hooks/mobile-hooks';
import SitePlanCardThumbnail from './SitePlanCardThumbnail';

interface UpcomingProjectCardProps {
  project: UpcomingProject;
  onClick?: (project: UpcomingProject) => void;
}

export const UpcomingProjectCard: React.FC<UpcomingProjectCardProps> = ({ project, onClick }) => {
  const { currentTheme } = useAppTheme();
  const isMobile = useIsMobile();
  const meta = [project.district, project.topYear ? `TOP: ${project.topYear}` : null, project.region]
    .filter(Boolean)
    .join(' • ');

  return (
    <Card
      onClick={() => onClick?.(project)}
      sx={{
        cursor: onClick ? 'pointer' : 'default',
        transition: isMobile ? 'all 150ms ease' : 'all 0.3s ease',
        position: 'relative',
        overflow: 'hidden',
        border: 'none',
        // Animated running border (skipped on mobile for perf)
        ...(!isMobile && {
          '&::before': {
            content: '""',
            position: 'absolute',
            top: '50%',
            left: '50%',
            width: 'max(200%, 600px)',
            height: 'max(200%, 600px)',
            transform: 'translate(-50%, -50%)',
            zIndex: 0,
            background:
              'conic-gradient(from 0deg, var(--color-accent), transparent 20%, transparent 80%, var(--color-accent))',
            animation: 'runningBorder 3s linear infinite',
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            inset: '2.5px',
            zIndex: 0,
            borderRadius: 'inherit',
            background:
              currentTheme === 'light'
                ? 'var(--color-surface, #ffffff)'
                : 'var(--color-bg-card, #1e1e1e)',
          },
          '& > *': {
            position: 'relative',
            zIndex: 1,
          },
          '@keyframes runningBorder': {
            from: { transform: 'translate(-50%, -50%) rotate(0deg)' },
            to: { transform: 'translate(-50%, -50%) rotate(360deg)' },
          },
        }),
        '&:hover': onClick
          ? {
              transform: isMobile ? 'none' : 'translateY(-8px)',
              boxShadow:
                '0 8px 24px rgba(var(--color-shadow-rgb), 0.3), 0 0 20px rgba(var(--color-accent-rgb, 255,152,0), 0.5)',
            }
          : undefined,
      }}
    >
      {/* Corner ribbon */}
      <Box
        sx={{
          '&&': { position: 'absolute' },
          top: 0,
          left: 0,
          zIndex: 2,
          width: 110,
          height: 110,
          overflow: 'hidden',
          pointerEvents: 'none',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            borderStyle: 'solid',
            borderWidth: '6px',
            borderColor:
              'color-mix(in srgb, var(--color-accent) 70%, #000) transparent transparent color-mix(in srgb, var(--color-accent) 70%, #000)',
          },
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: 18,
            left: -28,
            width: 140,
            textAlign: 'center',
            transform: 'rotate(-45deg)',
            bgcolor: 'var(--color-accent)',
            color: '#fff',
            fontSize: '0.75rem',
            fontWeight: 700,
            letterSpacing: '0.08em',
            py: 0.6,
            boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
            background:
              'linear-gradient(135deg, color-mix(in srgb, var(--color-accent) 85%, #fff) 0%, var(--color-accent) 50%, color-mix(in srgb, var(--color-accent) 85%, #000) 100%)',
            overflow: 'hidden',
            ...(!isMobile && {
              '&::after': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: '-100%',
                width: '100%',
                height: '100%',
                background:
                  'linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)',
                animation: 'ribbonShimmer 3s ease-in-out infinite',
              },
              '@keyframes ribbonShimmer': {
                '0%': { left: '-100%' },
                '50%, 100%': { left: '100%' },
              },
            }),
          }}
        >
          UPCOMING
        </Box>
      </Box>

      {/* Image area (with 4px inset to reveal running border) */}
      <SitePlanCardThumbnail
        projectName={project.name}
        imageUrl={project.imageUrl}
        mapLatitude={project.mapLatitude}
        mapLongitude={project.mapLongitude}
        upcoming
      />

      <CardContent>
        <Typography
          variant="subtitle1"
          sx={{ fontWeight: 600, mb: 1.5, color: 'var(--color-text-strong)' }}
        >
          {project.name}
        </Typography>
        {meta && (
          <Typography
            variant="body2"
            sx={{ color: 'rgba(var(--color-text-rgb), 0.7)', mb: 0.5 }}
          >
            {meta}
          </Typography>
        )}
        {project.street && (
          <Typography variant="body2" sx={{ color: 'rgba(var(--color-text-rgb), 0.7)' }}>
            {project.street}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

export default UpcomingProjectCard;
